'use client';

import { useEffect, useRef, useState } from 'react';
import scrollToTopOfProductGrid from 'components/consumer/ProductCardGridUI/helpers/scrollToTopOfProductGrid';
import { NO_RESULTS_DELAY_MS } from 'commons/constants';
import { isSortOnlyFilterStateChange } from '../components/AdvancedSearchProductHits/utils';

/**
 * Manages filter transition state for the Algolia search results component.
 * Tracks when filters change, resets pagination, and controls skeleton/no-results display.
 *
 * Accepts refs (isPopRef, scrollRestoredRef, targetPagesRef) by reference — they are
 * intentionally excluded from the effect deps array because we always want their
 * current value at fire time, not to re-run the effect when they change.
 */
const useFilterTransitionState = ({
  filterStateKey,
  setIndexUiState,
  items,
  status,
  currentProductIndex,
  resultsIndex,
  activeTypeFilter,
  pagesUrlParam,
  updateUrlParamImmediate,
  isPopRef,
  scrollRestoredRef,
  targetPagesRef,
}) => {
  const isFirstMountRef = useRef(true);
  const previousFilterStateKeyRef = useRef(null);

  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  const [isFilterTransitioning, setIsFilterTransitioning] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);

  // When any filter changes, reset pagination to page 0 (12 products), scroll to top,
  // and indicate transition. targetPagesRef is reset synchronously so the cascade
  // cannot fire with a stale value before the URL update propagates.
  // setIndexUiState({ page: 0 }) is critical: Configure-based filters (RTS, width)
  // don't trigger useInfiniteHits to reset its internal page counter on their own.
  // Without this, the Algolia request goes out with the stale page number.
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      previousFilterStateKeyRef.current = filterStateKey;
      targetPagesRef.current = pagesUrlParam;
      // Clear stale page state from the shared InstantSearch instance on mount,
      // unless back-navigation is restoring a multi-page URL.
      if (!(isPopRef.current && pagesUrlParam > 0)) {
        setIndexUiState(prev =>
          prev?.page === 0 ? prev : { ...prev, page: 0 }
        );
      }
      return;
    }

    if (filterStateKey !== previousFilterStateKeyRef.current) {
      const onlySortChanged = isSortOnlyFilterStateChange(
        previousFilterStateKeyRef.current,
        filterStateKey
      );
      previousFilterStateKeyRef.current = filterStateKey;

      // SearchAlgoliaIndexSync already resets the page and searches once.
      // setIndexUiState would send that same sort again.
      if (onlySortChanged) {
        targetPagesRef.current = 0;
        setIsFilterTransitioning(true);
        scrollToTopOfProductGrid(false);
        return;
      }

      if (isPopRef.current && !scrollRestoredRef.current) {
        if (activeTypeFilter) {
          setIndexUiState(prev => {
            if (prev?.refinementList?.type?.[0] === activeTypeFilter)
              return prev;
            return {
              ...prev,
              refinementList: {
                ...(prev.refinementList || {}),
                type: [activeTypeFilter],
              },
            };
          });
        }
        return;
      }

      targetPagesRef.current = 0;
      setIsFilterTransitioning(true);
      setIndexUiState(prev => {
        const next = { ...prev, page: 0 };
        if (activeTypeFilter && !next.refinementList?.type?.length) {
          next.refinementList = {
            ...(next.refinementList || {}),
            type: [activeTypeFilter],
          };
        }
        return next;
      });
      updateUrlParamImmediate?.({ pages: undefined });
      scrollToTopOfProductGrid(false);
    }
  }, [
    filterStateKey,
    pagesUrlParam,
    updateUrlParamImmediate,
    setIndexUiState,
    activeTypeFilter,
    isPopRef,
    scrollRestoredRef,
    targetPagesRef,
  ]);

  // Clear filter transition state once new results have arrived (or when response is 0 hits).
  // Sort keeps the previous hits until the replica responds, so `items` still
  // looks populated. Wait until those hits come from the selected index.
  useEffect(() => {
    if (!isFilterTransitioning) return;
    if (
      currentProductIndex &&
      resultsIndex &&
      resultsIndex !== currentProductIndex
    ) {
      return;
    }
    if (items?.length > 0) {
      setIsFilterTransitioning(false);
    } else if (status === 'idle' && items !== undefined) {
      // Algolia returned with 0 hits after filter change; stop showing skeleton
      setIsFilterTransitioning(false);
    }
  }, [isFilterTransitioning, items, status, currentProductIndex, resultsIndex]);

  // Track when search has completed (mark once Algolia returns any result set, even empty)
  useEffect(() => {
    if (status === 'idle' && !hasSearchCompleted && items !== undefined) {
      setHasSearchCompleted(true);
    }
  }, [status, hasSearchCompleted, items]);

  // Delay showing "No results found" to avoid brief flash while Algolia is still loading
  const isSearchPending = status === 'loading' || status === 'stalled';
  useEffect(() => {
    if (items?.length > 0 || isSearchPending) {
      setShowNoResults(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setShowNoResults(true);
    }, NO_RESULTS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [items?.length, isSearchPending]);

  return { isFilterTransitioning, hasSearchCompleted, showNoResults };
};

export default useFilterTransitionState;
